import React, { useState, useEffect, useRef } from "react";

function WoWEventsSection({ sessionId }) {
  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "https://localhost:3443";

  const authHeaders = {
    Authorization: `Bearer ${sessionId || ""}`,
    "Content-Type": "application/json",
  };

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [notification, setNotification] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: "",
    date: "",
    time: "",
    description: "",
  });

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [roster, setRoster] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [refreshCooldown, setRefreshCooldown] = useState(0);

  // Protection contre les appels simultanés avec useRef
  const isFetchingRef = useRef(false);
  const cooldownRef = useRef(null);

  // Fonction pour récupérer les événements Discord
  const fetchEvents = async () => {
    // Protection contre les appels simultanés avec useRef
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/events`, {
        headers: authHeaders,
      });
      const data = await response.json();

      if (data.success) {
        setEvents(data.events);
      } else {
        console.error(
          "Erreur lors de la récupération des événements:",
          data.message
        );
        setEvents([]);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des événements:", error);
      setEvents([]);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  // Fonction pour récupérer le roster actif
  const fetchRoster = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/roster`, {
        headers: authHeaders,
      });
      const data = await response.json();
      if (data.Members) {
        setRoster(data.Members.filter((m) => !m.Disabled));
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du roster:", error);
      showNotification("❌ Erreur lors du chargement du roster.", "error");
    }
  };

  // Fonction pour supprimer un événement
  const handleDeleteEvent = async () => {
    if (!selectedEvent || isDeleting) return;
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${selectedEvent.id}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      const result = await response.json();
      if (result.success) {
        setSelectedEvent(null);
        setDeleteConfirm(false);
        showNotification("✅ Événement supprimé avec succès.", "success");
        await fetchEvents();
      } else {
        showNotification(`❌ Erreur: ${result.message}`, "error");
      }
    } catch (error) {
      showNotification("❌ Erreur lors de la suppression. Veuillez réessayer.", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  // Ferme le modal et réinitialise les états associés
  const handleCloseModal = () => {
    setSelectedEvent(null);
    setDeleteConfirm(false);
  };

  const handleRefresh = async () => {
    if (refreshCooldown > 0) return;
    await fetchEvents();
    setRefreshCooldown(10);
    cooldownRef.current = setInterval(() => {
      setRefreshCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Charger les événements au montage du composant
  useEffect(() => {
    fetchEvents();
    fetchRoster();
    setRefreshCooldown(10);
    cooldownRef.current = setInterval(() => {
      setRefreshCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(cooldownRef.current);
  }, []);

  // Fonction pour afficher une notification
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();

    try {
      // Appeler l'API pour créer l'événement Discord
      const response = await fetch(`${API_BASE_URL}/api/events/create`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          title: newEvent.title,
          date: newEvent.date,
          time: newEvent.time,
          description: newEvent.description,
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Réinitialiser le formulaire
        setNewEvent({
          title: "",
          date: "",
          time: "",
          description: "",
        });
        setShowCreateForm(false);

        // Recharger les événements avec un délai pour éviter le rate limiting
        setTimeout(async () => {
          await fetchEvents();
        }, 5000); // 5 secondes

        // Afficher un message de succès
        showNotification(
          "✅ Événement créé avec succès ! Il a été ajouté à Discord et annoncé dans le canal approprié.",
          "success"
        );
      } else {
        showNotification(
          `❌ Erreur lors de la création de l'événement: ${result.message}`,
          "error"
        );
      }
    } catch (error) {
      showNotification(
        "❌ Erreur lors de la création de l'événement. Veuillez réessayer.",
        "error"
      );
    }
  };

  return (
    <div className="wow-events-section">
      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <header className="section-header">
        <h1>📅 Événements</h1>
        <p>Gestionnaire d'événements WoW</p>
      </header>

      <div className="events-controls">
        <button
          className="refresh-btn"
          onClick={handleRefresh}
          disabled={refreshCooldown > 0}
        >
          {refreshCooldown > 0 ? `🔄 ${refreshCooldown}s` : "🔄"}
        </button>
        <button
          className="create-event-btn"
          onClick={() => setShowCreateForm(true)}
        >
          ➕ Créer un événement
        </button>
      </div>

      {showCreateForm && (
        <div className="create-event-form">
          <h3>Créer un nouvel événement</h3>
          <form onSubmit={handleCreateEvent}>
            <div className="form-group">
              <label>Sujet:</label>
              <input
                type="text"
                value={newEvent.title}
                onChange={(e) =>
                  setNewEvent({ ...newEvent, title: e.target.value })
                }
                placeholder="Titre de l'événement"
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Date de début:</label>
                <input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, date: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Heure de début:</label>
                <input
                  type="time"
                  value={newEvent.time}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, time: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Description:</label>
              <textarea
                value={newEvent.description}
                onChange={(e) =>
                  setNewEvent({ ...newEvent, description: e.target.value })
                }
                placeholder="Description de l'événement"
                rows="4"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="submit-btn">
                Créer l'événement
              </button>
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setShowCreateForm(false)}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="events-container">
        <h3>Événements à venir</h3>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Chargement des événements...</p>
          </div>
        ) : events.length === 0 ? (
          <div className="no-results">
            <p>Aucun événement à venir pour le moment.</p>
          </div>
        ) : (
          <div className="events-grid">
            {events.map((event) => (
              <div key={event.id} className="event-card">
                {/* Image de couverture WoW */}
                <img
                  src="/cover.png"
                  alt="Couverture WoW"
                  className="event-cover"
                  style={{
                    width: "100%",
                    borderRadius: "10px",
                    marginBottom: "15px",
                    objectFit: "cover",
                    maxHeight: "180px",
                  }}
                />
                <div className="event-header">
                  <h4>{event.title}</h4>
                  <span className={`event-status ${event.status}`}>
                    {event.status === "upcoming" ? "À venir" : "Terminé"}
                  </span>
                </div>
                <div className="event-details">
                  <p>
                    <strong>📅 Date:</strong>{" "}
                    {new Date(event.scheduled_start_time).toLocaleDateString(
                      "fr-FR",
                      {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )}
                  </p>
                  <p>
                    <strong>⏰ Heure:</strong> {event.time}
                  </p>
                  <p>
                    <strong>👥 Participants:</strong>{" "}
                    {Object.values(event.presences || {}).filter((s) => s === "presence_present").length}
                  </p>
                </div>
                <div className="event-description">
                  <p>{event.description}</p>
                </div>
                <div className="event-actions">
                  <button
                    className="details-btn"
                    onClick={() => { setSelectedEvent(event); setDeleteConfirm(false); }}
                  >
                    Détails
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Détails */}
      {selectedEvent && (
        <div
          className="modal-overlay"
          onClick={handleCloseModal}
        >
          <div
            className="modal event-details-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{selectedEvent.title}</h3>
              <button
                className="close-btn"
                onClick={handleCloseModal}
              >
                ✕
              </button>
            </div>
            <div className="event-details-body">
              <div className="event-details-meta">
                <span>
                  📅{" "}
                  {new Date(selectedEvent.scheduled_start_time).toLocaleDateString("fr-FR", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
                <span>⏰ {selectedEvent.time}</span>
              </div>

              {[
                { label: "🛡️ TANK", key: "Tank" },
                { label: "💚 HEAL", key: "Heal" },
                { label: "⚔️ DPS", key: "DPS" },
              ].map((roleGroup) => {
                const members = roster.filter((m) => m.Role === roleGroup.key);
                return (
                  <div key={roleGroup.key} className="presence-group">
                    <h4 className="presence-group-title">{roleGroup.label}</h4>
                    {members.length === 0 ? (
                      <p className="presence-none">Aucun membre</p>
                    ) : (
                      <ul className="presence-list">
                        {members.map((member) => {
                          const status = selectedEvent.presences?.[member.Name];
                          const emoji =
                            status === "presence_present" ? "✅" :
                            status === "presence_absent" ? "❌" :
                            status === "presence_incertain" ? "❓" : "—";
                          return (
                            <li key={member.Name} className="presence-item">
                              <span className="presence-name">
                                • {member.Name}{" "}
                                <span className="presence-class">({member.Class})</span>
                              </span>
                              <span className="presence-emoji">{emoji}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}

              <div className="event-details-footer">
                {!deleteConfirm ? (
                  <button
                    className="delete-event-btn"
                    onClick={() => setDeleteConfirm(true)}
                  >
                    🗑️ Supprimer l'événement
                  </button>
                ) : (
                  <div className="delete-confirm">
                    <p>Êtes-vous sûr ? Cette action est irréversible.</p>
                    <div className="delete-confirm-actions">
                      <button className="confirm-delete-btn" onClick={handleDeleteEvent} disabled={isDeleting}>
                        {isDeleting ? "Suppression..." : "Confirmer"}
                      </button>
                      <button
                        className="cancel-btn"
                        onClick={() => setDeleteConfirm(false)}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WoWEventsSection;
